import React from "react";
import { useRoute, Link } from "wouter";
import { Layout } from "@/components/layout";
import {
  useGetLesson,
  getGetLessonQueryKey,
  useListMySnippets,
  getListMySnippetsQueryKey,
  useGetCourse,
  getGetCourseQueryKey,
  useSubmitHomework,
  getLessonAttachmentDownload,
  type LessonAttachment,
  type HomeworkSubmission,
} from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { Calendar as CalendarIcon, FileText, Code, ArrowLeft, ArrowRight, ExternalLink, Terminal, Paperclip, Download, CheckCircle2, GraduationCap, Loader2 } from "lucide-react";
import { dateLongFmt } from "@/lib/format";
import { useUser } from "@clerk/react";
import { CodeSandbox } from "@/components/sandbox/code-sandbox";
import { LessonVideoPlayer } from "@/components/lesson-video-player";
import {
  isJoinable,
  googleCalendarUrl,
  formatLocalSessionTime,
} from "@/lib/calendar";

function LiveSessionBlock({
  lesson,
}: {
  lesson: {
    title: string;
    summary: string;
    orderIndex: number;
    zoomUrl: string | null;
    zoomScheduledAt: string | null;
    zoomLiveStartedAt: string | null;
  };
}) {
  // Recompute joinability every 30s so the button transitions automatically
  // at the start of the [-15min, +2hr] window. The poll on `useGetLesson`
  // refreshes server-side state (zoomLiveStartedAt) on the same cadence.
  const [, force] = React.useState(0);
  React.useEffect(() => {
    const t = setInterval(() => force((n) => n + 1), 30_000);
    return () => clearInterval(t);
  }, []);

  const joinable = isJoinable(lesson);
  const teacherStarted = !!lesson.zoomLiveStartedAt;

  if (joinable && lesson.zoomUrl) {
    return (
      <div className="bg-secondary/15 border-2 border-secondary rounded-2xl p-8 md:p-12 text-center shadow-sm flex flex-col items-center justify-center">
        <div className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-red-600 mb-4">
          <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" aria-hidden="true" />
          {teacherStarted ? "Live now" : "Session window open"}
        </div>
        <h3 className="text-2xl font-bold mb-2">
          {teacherStarted ? "Class is in session" : "Time to join"}
        </h3>
        <p className="text-lg text-muted-foreground mb-8">
          {teacherStarted && lesson.zoomLiveStartedAt
            ? `Your teacher started the Zoom session ${formatLocalSessionTime(lesson.zoomLiveStartedAt)}.`
            : lesson.zoomScheduledAt
              ? `Scheduled ${formatLocalSessionTime(lesson.zoomScheduledAt)}.`
              : null}
        </p>
        <Button size="lg" asChild className="h-14 px-8 text-base">
          <a href={lesson.zoomUrl} target="_blank" rel="noopener noreferrer">
            Join live now <ExternalLink className="ml-2 w-5 h-5" />
          </a>
        </Button>
      </div>
    );
  }

  return (
    <div className="bg-card border rounded-2xl p-8 md:p-12 text-center shadow-sm flex flex-col items-center justify-center">
      <div className="w-20 h-20 bg-primary/10 text-primary rounded-full flex items-center justify-center mb-6">
        <CalendarIcon className="w-10 h-10" />
      </div>
      <h3 className="text-2xl font-bold mb-2">Live Zoom Session</h3>
      {lesson.zoomScheduledAt ? (
        <p className="text-lg text-muted-foreground mb-6">
          Scheduled for {formatLocalSessionTime(lesson.zoomScheduledAt)}
        </p>
      ) : (
        <p className="text-lg text-muted-foreground mb-6">Schedule pending</p>
      )}
      {lesson.zoomScheduledAt && (
        <Button asChild variant="outline" className="mb-4 rounded-full">
          <a
            href={googleCalendarUrl({
              title: `Live · Lesson ${String(lesson.orderIndex).padStart(2, "0")}: ${lesson.title}`,
              start: new Date(lesson.zoomScheduledAt),
              description: lesson.summary,
              location: lesson.zoomUrl ?? undefined,
            })}
            target="_blank"
            rel="noopener noreferrer"
          >
            <CalendarIcon className="w-4 h-4 mr-2" /> Add to Google Calendar
          </a>
        </Button>
      )}
      {lesson.zoomUrl ? (
        <>
          <Button size="lg" disabled className="h-14 px-8 text-base mb-3">
            Join opens 15 min before start
          </Button>
          <p className="text-xs text-muted-foreground">
            The Join button activates 15 minutes before the scheduled time.
          </p>
        </>
      ) : (
        <Button size="lg" disabled className="h-14 px-8 text-base">
          Link not available yet
        </Button>
      )}
    </div>
  );
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function AttachmentsCard({
  lessonId,
  attachments,
}: {
  lessonId: number;
  attachments: LessonAttachment[];
}) {
  const { toast } = useToast();
  const [downloadingId, setDownloadingId] = React.useState<number | null>(null);

  const handleDownload = async (att: LessonAttachment) => {
    setDownloadingId(att.id);
    try {
      const { url } = await getLessonAttachmentDownload(lessonId, att.id);
      // Open the short-lived signed URL directly — the browser downloads
      // straight from object storage.
      window.open(url, "_blank", "noopener,noreferrer");
    } catch {
      toast({
        title: "Download failed",
        description: "Could not get a download link. Please try again.",
        variant: "destructive",
      });
    } finally {
      setDownloadingId(null);
    }
  };

  if (attachments.length === 0) return null;

  return (
    <div className="bg-card border rounded-2xl p-6 shadow-sm">
      <h3 className="font-bold text-lg mb-6 flex items-center gap-2">
        <Paperclip className="w-5 h-5 text-primary" /> Downloads
      </h3>
      <ul className="space-y-3">
        {attachments.map((att) => (
          <li key={att.id}>
            <button
              type="button"
              onClick={() => handleDownload(att)}
              disabled={downloadingId === att.id}
              className="w-full text-left p-4 rounded-xl border bg-muted/30 hover:bg-muted transition-colors group disabled:opacity-60"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-medium mb-1 group-hover:text-primary transition-colors truncate">
                    {att.title}
                  </div>
                  <div className="text-xs text-muted-foreground truncate">
                    {att.fileName} · {formatFileSize(att.fileSizeBytes)}
                  </div>
                </div>
                {downloadingId === att.id ? (
                  <Loader2 className="w-4 h-4 shrink-0 mt-1 animate-spin text-muted-foreground" aria-hidden="true" />
                ) : (
                  <Download className="w-4 h-4 shrink-0 mt-1 text-muted-foreground group-hover:text-primary transition-colors" aria-hidden="true" />
                )}
              </div>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function HomeworkSubmissionBlock({
  homeworkId,
  lessonId,
  submission,
  isSignedIn,
}: {
  homeworkId: number;
  lessonId: number;
  submission: HomeworkSubmission | null;
  isSignedIn: boolean;
}) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [editing, setEditing] = React.useState(false);
  const [content, setContent] = React.useState("");

  const submitMutation = useSubmitHomework({
    mutation: {
      onSuccess: () => {
        setEditing(false);
        toast({ title: "Turned in", description: "Your homework was submitted." });
        void queryClient.invalidateQueries({
          queryKey: getGetLessonQueryKey(lessonId),
        });
      },
      onError: (err: unknown) => {
        const message =
          err && typeof err === "object" && "message" in err
            ? String((err as { message: unknown }).message)
            : "Could not submit. Please try again.";
        toast({ title: "Submission failed", description: message, variant: "destructive" });
      },
    },
  });

  if (!isSignedIn) {
    return (
      <p className="text-xs text-muted-foreground mt-3 italic">
        Sign in to turn in this homework.
      </p>
    );
  }

  const showForm = editing || !submission;

  if (showForm) {
    return (
      <form
        className="mt-3 space-y-2"
        onSubmit={(e) => {
          e.preventDefault();
          if (!content.trim()) return;
          submitMutation.mutate({ id: homeworkId, data: { content: content.trim() } });
        }}
      >
        <Textarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder="Paste your answer, code, or a link to your work…"
          rows={4}
          maxLength={20000}
          className="text-sm font-mono"
        />
        <div className="flex items-center gap-2">
          <Button
            type="submit"
            size="sm"
            disabled={!content.trim() || submitMutation.isPending}
            className="rounded-full"
          >
            {submitMutation.isPending ? (
              <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Turning in…</>
            ) : submission ? (
              "Resubmit"
            ) : (
              "Turn in"
            )}
          </Button>
          {submission && (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="rounded-full"
              onClick={() => setEditing(false)}
            >
              Cancel
            </Button>
          )}
        </div>
        {submission && (
          <p className="text-[11px] text-muted-foreground">
            Resubmitting replaces your previous answer and clears any grade.
          </p>
        )}
      </form>
    );
  }

  const graded = submission.grade !== null;

  return (
    <div className="mt-3 space-y-3">
      <div className="p-3 rounded-xl border bg-muted/30">
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-2">
          <CheckCircle2 className="w-3.5 h-3.5 text-secondary-foreground" aria-hidden="true" />
          Turned in {dateLongFmt.format(new Date(submission.submittedAt))}
        </div>
        <p className="text-sm whitespace-pre-wrap break-words line-clamp-4">{submission.content}</p>
      </div>
      {graded ? (
        <div className="p-3 rounded-xl border-2 border-secondary bg-secondary/10">
          <div className="flex items-center justify-between gap-2 mb-1">
            <div className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
              <GraduationCap className="w-3.5 h-3.5" aria-hidden="true" /> Grade
            </div>
            <Badge className="text-sm font-bold">{submission.grade}</Badge>
          </div>
          {submission.gradeFeedback && (
            <p className="text-sm text-muted-foreground whitespace-pre-wrap mt-2">
              {submission.gradeFeedback}
            </p>
          )}
        </div>
      ) : (
        <p className="text-xs text-muted-foreground italic">Waiting for your teacher to grade this.</p>
      )}
      <Button
        type="button"
        size="sm"
        variant="outline"
        className="rounded-full"
        onClick={() => {
          setContent(submission.content);
          setEditing(true);
        }}
      >
        Edit &amp; resubmit
      </Button>
    </div>
  );
}

export default function LessonDetail() {
  const [, params] = useRoute("/lessons/:id");
  const id = parseInt(params?.id || "0", 10);
  
  const { data: lesson, isLoading, isError } = useGetLesson(id, {
    query: {
      enabled: !!id,
      queryKey: getGetLessonQueryKey(id),
      // Poll every 20s so students notice when the teacher starts the Zoom session.
      refetchInterval: (query) =>
        query.state.data?.course.format === "live" ? 20_000 : false,
      refetchIntervalInBackground: false,
    },
  });
  const { isSignedIn } = useUser();
  const { data: mySnippets } = useListMySnippets({
    query: {
      enabled: !!isSignedIn,
      queryKey: getListMySnippetsQueryKey(),
    },
  });
  const savedForLesson =
    mySnippets?.find((s) => s.lessonId === id) ?? null;

  const courseSlug = lesson?.course.slug;
  const { data: course } = useGetCourse(courseSlug ?? "", {
    query: {
      enabled: !!courseSlug,
      queryKey: getGetCourseQueryKey(courseSlug ?? ""),
    },
  });

  if (isLoading) return <Layout><div className="p-24 text-center" role="status" aria-live="polite">Loading lesson&hellip;</div></Layout>;
  if (isError || !lesson) return <Layout><div className="p-24 text-center">Lesson not found.</div></Layout>;

  const isLive = lesson.course.format === "live";

  const orderedLessons = course
    ? [...course.lessons].sort((a, b) => a.orderIndex - b.orderIndex)
    : [];
  const currentIdx = orderedLessons.findIndex((l) => l.id === lesson.id);
  const nextLesson = currentIdx >= 0 ? orderedLessons[currentIdx + 1] ?? null : null;
  const prevLesson = currentIdx > 0 ? orderedLessons[currentIdx - 1] ?? null : null;

  return (
    <Layout>
      <div className="border-b border-border relative overflow-hidden">
        <div className="absolute inset-0 bg-grid pointer-events-none opacity-50" aria-hidden="true" />
        <div className="container mx-auto px-4 py-12 md:py-16 relative z-10">
          <Link
            href={`/learn/${lesson.course.slug}`}
            className="inline-flex items-center font-mono text-xs uppercase tracking-widest text-muted-foreground hover:text-primary mb-8 transition-colors"
          >
            <ArrowLeft className="w-3.5 h-3.5 mr-2" /> Back to classroom · {lesson.course.title}
          </Link>
          <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-muted-foreground mb-4 flex flex-wrap items-center gap-x-3 gap-y-1">
            <span className="text-primary">[ lesson {String(lesson.orderIndex).padStart(2, "0")} ]</span>
            <span>/</span>
            <span>{isLive ? "live · zoom session" : "video lecture"}</span>
          </div>
          <h1 className="font-display text-4xl md:text-6xl font-bold leading-[1.02] tracking-tight mb-6 text-balance">{lesson.title}</h1>
          <p className="text-xl text-muted-foreground max-w-3xl leading-relaxed">{lesson.summary}</p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12 grid grid-cols-1 lg:grid-cols-3 gap-12">
        {/* Main Content Area */}
        <div className="lg:col-span-2 space-y-12">
          {/* Media/Class Block */}
          <section>
            {isLive ? (
              <LiveSessionBlock lesson={lesson} />
            ) : (
              <LessonVideoPlayer
                lessonId={lesson.id}
                hasVideo={!!lesson.hasVideo}
                durationMinutes={lesson.videoDurationMinutes}
                courseSlug={lesson.course.slug}
              />
            )}
          </section>

          {/* Instructions / Summary */}
          <section className="prose dark:prose-invert max-w-none">
            <h2 className="text-2xl font-bold border-b pb-4 mb-6">Lesson Overview</h2>
            <p className="text-muted-foreground leading-relaxed text-lg">
              {lesson.summary}
            </p>
          </section>

          {(lesson.sandboxLanguage === "python" || lesson.sandboxLanguage === "web") && lesson.sandboxStarter && (
            <section>
              <h2 className="text-2xl font-bold border-b pb-4 mb-6 flex items-center gap-2">
                <Terminal className="w-5 h-5 text-primary" /> Code Sandbox
              </h2>
              <CodeSandbox
                language={lesson.sandboxLanguage}
                starter={lesson.sandboxStarter}
                lessonId={lesson.id}
                lessonTitle={lesson.title}
                initialSnippet={savedForLesson}
              />
            </section>
          )}

          <nav
            aria-label="Lesson navigation"
            className="border-t pt-8 flex flex-col sm:flex-row gap-3 sm:items-stretch sm:justify-between"
          >
            {prevLesson ? (
              <Button asChild variant="outline" size="lg" className="rounded-full sm:max-w-[48%] h-auto py-3 justify-start text-left">
                <Link href={`/lessons/${prevLesson.id}`}>
                  <ArrowLeft className="w-4 h-4 mr-3 shrink-0" />
                  <span className="flex flex-col items-start min-w-0">
                    <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                      Lesson {String(prevLesson.orderIndex).padStart(2, "0")} · Previous
                    </span>
                    <span className="font-medium truncate">{prevLesson.title}</span>
                  </span>
                </Link>
              </Button>
            ) : (
              <Button asChild variant="outline" size="lg" className="rounded-full sm:max-w-[48%]">
                <Link href={`/learn/${lesson.course.slug}`}>
                  <ArrowLeft className="w-4 h-4 mr-2" /> Back to classroom
                </Link>
              </Button>
            )}
            {nextLesson ? (
              <Button asChild size="lg" className="rounded-full sm:max-w-[48%] h-auto py-3 justify-end text-left">
                <Link href={`/lessons/${nextLesson.id}`}>
                  <span className="flex flex-col items-end min-w-0">
                    <span className="font-mono text-[10px] uppercase tracking-widest text-primary-foreground/80">
                      Lesson {String(nextLesson.orderIndex).padStart(2, "0")} · Next
                    </span>
                    <span className="font-medium truncate">{nextLesson.title}</span>
                  </span>
                  <ArrowRight className="w-4 h-4 ml-3 shrink-0" />
                </Link>
              </Button>
            ) : (
              orderedLessons.length > 0 && (
                <Button asChild size="lg" className="rounded-full sm:max-w-[48%]">
                  <Link href={`/learn/${lesson.course.slug}`}>
                    Course complete · Back to classroom <ArrowRight className="w-4 h-4 ml-2" />
                  </Link>
                </Button>
              )
            )}
          </nav>
        </div>

        {/* Sidebar */}
        <div className="space-y-8">
          <div className="bg-card border rounded-2xl p-6 shadow-sm">
            <h3 className="font-bold text-lg mb-6 flex items-center gap-2">
              <FileText className="w-5 h-5 text-primary" /> Required Reading
            </h3>
            {lesson.readings.length > 0 ? (
              <ul className="space-y-4">
                {lesson.readings.map(reading => {
                  const inner = (
                    <>
                      <div className="font-medium mb-1 group-hover:text-primary transition-colors">{reading.title}</div>
                      <div className="text-xs text-muted-foreground flex items-center justify-between">
                        <span>{reading.estimatedMinutes} min read</span>
                        {reading.url && <ExternalLink className="w-3 h-3" aria-hidden="true" />}
                      </div>
                    </>
                  );
                  return (
                    <li key={reading.id} className="group">
                      {reading.url ? (
                        <a
                          href={reading.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block p-4 rounded-xl border bg-muted/30 hover:bg-muted transition-colors"
                        >
                          {inner}
                        </a>
                      ) : (
                        <div className="block p-4 rounded-xl border bg-muted/30">{inner}</div>
                      )}
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p className="text-sm text-muted-foreground italic">No readings for this lesson.</p>
            )}
          </div>

          <AttachmentsCard lessonId={lesson.id} attachments={lesson.attachments} />

          <div className="bg-card border rounded-2xl p-6 shadow-sm">
            <h3 className="font-bold text-lg mb-6 flex items-center gap-2">
              <Code className="w-5 h-5 text-secondary" /> Homework
            </h3>
            {lesson.homework.length > 0 ? (
              <ul className="space-y-6">
                {lesson.homework.map(hw => {
                  const mySubmission =
                    lesson.mySubmissions.find((s) => s.homeworkId === hw.id) ?? null;
                  return (
                    <li key={hw.id} className="border-b last:border-0 pb-6 last:pb-0">
                      <div className="flex justify-between items-start mb-2">
                        <div className="font-bold">{hw.title}</div>
                        <Badge variant="outline" className="text-xs shrink-0">{hw.estimatedMinutes} min</Badge>
                      </div>
                      <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-wrap">
                        {hw.instructions}
                      </p>
                      <HomeworkSubmissionBlock
                        homeworkId={hw.id}
                        lessonId={lesson.id}
                        submission={mySubmission}
                        isSignedIn={!!isSignedIn}
                      />
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p className="text-sm text-muted-foreground italic">No homework for this lesson.</p>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
